import type { MailBrand, MailSeries, MailTemplate, MailVariable } from '../types/mail'
import type { Salon } from '../types/site'

/**
 * Naam, adres en Instagram onderaan elke mail komen uit Instellingen › Salon & contact (één bron);
 * Facebook en de korte regel blijven van de mail zelf. Ontbreekt het adres, dan blijft het oude staan.
 */
export function withSalonFooter<B extends Pick<MailBrand, 'footer'>>(brand: B, salon: Pick<Salon, 'name' | 'address' | 'instagram'>): B {
  const address = [salon.name, salon.address].map((s) => s.trim()).filter(Boolean).join(' · ')
  return { ...brand, footer: { ...brand.footer, address: salon.address.trim() ? address : brand.footer.address, instagram: salon.instagram.trim() } }
}

/**
 * Automatische mails en reeksen: invulvelden ({voornaam}), en wat er moet kloppen voordat een
 * tekst live gaat. Puur; de server controleert hetzelfde bij PUT /admin/mails (422).
 */

export const MAIL_SUBJECT_MAX = 60
export const MAIL_PREHEADER_MAX = 110

export type MailIssue = { level: 'error' | 'warning'; text: string }

const VAR = /\{([a-z][a-z0-9_]*)\}/gi

/** Alle {invulvelden} in een tekst, zonder dubbele. */
export const variablesIn = (text: string) => Array.from(new Set(Array.from(text.matchAll(VAR), (m) => m[1])))

/** {voornaam} → "Eva". Onbekende velden blijven staan, zodat de controle ze kan vinden. */
export const fillVariables = (text: string, values: Record<string, string>) =>
  text.replace(VAR, (whole, key: string) => (key in values ? values[key] : whole))

/** Voor het voorbeeld en de testmail: elk veld met zijn voorbeeldwaarde. */
export const exampleValues = (variables: MailVariable[]) => Object.fromEntries(variables.map((v) => [v.key, v.example]))

type MailText = Pick<MailTemplate, 'subject' | 'preheader' | 'blocks' | 'variables'> & { button?: MailTemplate['button'] }

/** Wat er nog moet gebeuren aan een mail of reeks. Rood = opslaan kan niet. */
export function mailChecks(m: MailText & { enabled?: boolean; required?: boolean }): MailIssue[] {
  const out: MailIssue[] = []
  const known = new Set(m.variables.map((v) => v.key))
  const texts = [m.subject, m.preheader, ...m.blocks.flatMap((b) => [b.heading, b.body]), m.button?.label ?? '']

  if (!m.subject.trim()) out.push({ level: 'error', text: 'Nog geen onderwerp' })
  else if (m.subject.length > MAIL_SUBJECT_MAX) out.push({ level: 'warning', text: `Het onderwerp is lang: op de telefoon valt alles na ± ${MAIL_SUBJECT_MAX} tekens weg` })
  if (m.preheader.length > MAIL_PREHEADER_MAX) out.push({ level: 'warning', text: `De voorvertoningsregel is lang: ± ${MAIL_PREHEADER_MAX} tekens zijn zichtbaar` })
  if (m.blocks.every((b) => !b.body.trim() && !b.heading.trim())) out.push({ level: 'error', text: 'De mail heeft nog geen tekst' })

  const unknown = Array.from(new Set(texts.flatMap(variablesIn))).filter((k) => !known.has(k))
  if (unknown.length) out.push({ level: 'error', text: `Onbekend invulveld: ${unknown.map((k) => `{${k}}`).join(', ')}. Kies er een uit de lijst.` })

  if (m.button && !known.has(m.button.variable)) out.push({ level: 'error', text: 'De knop linkt naar een veld dat deze mail niet kent' })
  if (m.required && m.enabled === false) out.push({ level: 'error', text: 'Deze mail is verplicht en kan niet uit' })
  return out
}

type WithStandard = Pick<MailTemplate, 'subject' | 'preheader' | 'blocks' | 'standard'>

const textKey = (t: Pick<MailTemplate, 'subject' | 'preheader' | 'blocks'>) =>
  JSON.stringify([t.subject.trim(), t.preheader.trim(), t.blocks.map((b) => [b.heading.trim(), b.body.trim()])])

/** Staat de tekst nog zoals Laravel hem levert (beslissing M2)? */
export const isStandardText = (m: WithStandard) => textKey(m) === textKey(m.standard)

/** "Terug naar standaardtekst": onderwerp, voorvertoning en blokken uit Laravel; de rest blijft. */
export const withStandardText = <T extends WithStandard>(m: T): T => ({
  ...m,
  subject: m.standard.subject,
  preheader: m.standard.preheader,
  blocks: m.standard.blocks.map((b) => ({ ...b })),
})

/** Wat de server verstuurt: leeg onderwerp of alleen lege blokken = de standaardtekst. */
export function effectiveMailText(m: WithStandard): Pick<MailTemplate, 'subject' | 'preheader' | 'blocks'> {
  const empty = !m.subject.trim() || m.blocks.every((b) => !b.body.trim() && !b.heading.trim())
  return empty ? m.standard : { subject: m.subject, preheader: m.preheader, blocks: m.blocks }
}

/** Grenzen voor de wachttijd van een reeks. */
export const seriesWaitLimits = (s: Pick<MailSeries, 'waitUnit'>) => (s.waitUnit === 'uren' ? { min: 1, max: 72 } : { min: 0, max: 365 })

export function seriesChecks(s: MailSeries): MailIssue[] {
  const out = mailChecks(s)
  const { min, max } = seriesWaitLimits(s)
  if (!Number.isInteger(s.wait) || s.wait < min || s.wait > max) out.push({ level: 'error', text: `Wachttijd tussen ${min} en ${max} ${s.waitUnit}` })
  return out
}
